import React from 'react';
import Modal from 'react-modal';

import classnames from 'classnames';

import { domHooks } from '@nxte-nl/dom-hooks';

import { rootStyleVariables } from '../../constants/rootStyleVariables';
import { keyboardHooks } from '../../hooks/keyboardHooks/keyboardHooks';
import { routerHooks } from '../../hooks/routerHooks/routerHooks';
import styles from './DynamicalHeightFullScreenModalWindow.module.css';

export type Props = {
  children: JSX.Element;
  className?: string;
  headerHeight: number;
  isBlockOutsideClick?: boolean;
  isExpanded: boolean;
  offset: number;
  onClose: () => void;
  shouldCloseOnEsc?: boolean;
};

export const hooks = {
  useCloseOnUrlChange(
    onClose: () => void,
  ) {
    const listen = routerHooks.useListen();

    React.useEffect(() => {
      return listen(() => onClose());
    }, [listen, onClose]);
  },
  useOutsideClick(
    onClose: () => void,
    isBlockOutsideClick?: boolean,
  ) {
    const modalRef = React.useRef<HTMLDivElement | null>(null);
    const handleOutSideClick = hooks.useGetHandleOutSideClick(
      modalRef, onClose, isBlockOutsideClick,
    );
    React.useEffect(() => {
      window.addEventListener('click', handleOutSideClick);
      return () => {
        window.removeEventListener('click', handleOutSideClick);
      };
    }, [handleOutSideClick]);

    const setModalRef = React.useCallback((ref: HTMLDivElement) => {
      modalRef.current = ref;
    }, [modalRef]);

    return { setModalRef };
  },
  useGetHandleOutSideClick(
    modalRef: React.RefObject<HTMLDivElement | null>,
    onClose: () => void,
    isBlockOutsideClick = false,
  ) {
    return React.useCallback((event: MouseEvent) => {
      event.stopPropagation();
      if (modalRef.current
        && 'contains' in modalRef.current
        && !modalRef.current.contains(event.target as HTMLDivElement)
        && !isBlockOutsideClick) {
        onClose();
      }
    }, [modalRef, isBlockOutsideClick, onClose]);
  },
};

export const DynamicalHeightFullScreenModalWindow = React.memo((props: Props) => {
  const {
    isExpanded,
    children,
    offset,
    className,
    onClose,
    shouldCloseOnEsc = false,
    headerHeight,
    isBlockOutsideClick,
  } = props;

  domHooks.useDisableScrolling(isExpanded);
  hooks.useCloseOnUrlChange(onClose);
  keyboardHooks.useEscape(shouldCloseOnEsc && isExpanded, onClose);

  const { setModalRef } = hooks.useOutsideClick(onClose, isBlockOutsideClick);

  const modalClassNames = classnames(styles.dynamicalHeightFullScreenModalWindow, className);
  const style = {
    '--offset': `${offset}px`,
  } as React.CSSProperties;

  const contentStyle: React.CSSProperties = {
    'maxHeight': `calc(90vh - ${offset}px + ${headerHeight}px)`,
  };

  return (
    <Modal
      isOpen={isExpanded}
      shouldFocusAfterRender={false}
      overlayClassName={classnames(
        styles.overlayClassName, rootStyleVariables[CONFIG.applicationSiteGroup],
      )}
      className={modalClassNames}
      style={{ overlay: style, content: contentStyle }}
      onRequestClose={onClose}
      overlayRef={setModalRef}
    >
      {children}
    </Modal>
  );
});

DynamicalHeightFullScreenModalWindow.displayName = 'DynamicalHeightFullScreenModalWindow';
